import { useState, useEffect, useRef } from 'react';
import { Node, Edge } from '@xyflow/react';
import { FlowNodeType, FlowNodeTypeValue, EditorLookups } from './types';
import { getNodeInfo } from './metadata';

interface Props {
  selected: Node | null;
  lookups: EditorLookups;
  allNodes: Node[];
  allEdges: Edge[];
  onConfigChange: (nodeId: string, newConfig: string) => void;
  onDelete: (nodeId: string) => void;
}

/**
 * Encontra todos os queueIds de nodes TransferToQueue que estão upstream do nó atual.
 * Faz BFS reverso pelas edges (target → source).
 */
function findUpstreamQueueIds(
  currentNodeId: string,
  nodes: Node[],
  edges: Edge[],
): Set<string> {
  const queueIds = new Set<string>();
  const visited = new Set<string>();
  const stack: string[] = [currentNodeId];

  while (stack.length > 0) {
    const id = stack.pop()!;
    if (visited.has(id)) continue;
    visited.add(id);

    const incoming = edges.filter((e) => e.target === id);
    for (const edge of incoming) {
      const sourceNode = nodes.find((n) => n.id === edge.source);
      if (!sourceNode) continue;

      const sourceType = (sourceNode.data as { type?: FlowNodeTypeValue }).type;
      if (sourceType === FlowNodeType.TransferToQueue) {
        try {
          const cfg = JSON.parse((sourceNode.data as { config?: string }).config ?? '{}');
          if (cfg.queueId && typeof cfg.queueId === 'string') {
            queueIds.add(cfg.queueId);
          }
        } catch {
          /* ignore malformed config */
        }
        // Não traversal além do TransferToQueue (ele é terminal-ish no fluxo)
        continue;
      }

      stack.push(sourceNode.id);
    }
  }

  return queueIds;
}

export function PropertiesPanel({ selected, lookups, allNodes, allEdges, onConfigChange, onDelete }: Props) {
  if (!selected) {
    return (
      <aside className="wt-rf-props">
        <div className="wt-rf-props-empty">
          <strong>Nenhum nó selecionado</strong>
          <span>Clique em um nó para editar.</span>
        </div>
      </aside>
    );
  }

  const type = (selected.data as { type?: FlowNodeTypeValue }).type ?? FlowNodeType.SendMessage;
  const info = getNodeInfo(type);

  return (
    <aside className="wt-rf-props">
      <header className="wt-rf-props-head" style={{ borderLeftColor: info.color }}>
        <span style={{ color: info.color }}>{info.label}</span>
        <button className="wt-rf-btn-danger" onClick={() => onDelete(selected.id)}>Remover</button>
      </header>
      <div className="wt-rf-props-body">
        <NodeConfigForm
          nodeId={selected.id}
          type={type}
          configJson={(selected.data as { config?: string }).config ?? '{}'}
          lookups={lookups}
          allNodes={allNodes}
          allEdges={allEdges}
          onChange={onConfigChange}
        />
      </div>
    </aside>
  );
}

interface FormProps {
  nodeId: string;
  type: FlowNodeTypeValue;
  configJson: string;
  lookups: EditorLookups;
  allNodes: Node[];
  allEdges: Edge[];
  onChange: (nodeId: string, newConfig: string) => void;
}

function NodeConfigForm({ nodeId, type, configJson, lookups, allNodes, allEdges, onChange }: FormProps) {
  const [cfg, setCfg] = useState<Record<string, unknown>>({});

  useEffect(() => {
    try {
      setCfg(JSON.parse(configJson || '{}'));
    } catch {
      setCfg({});
    }
  }, [nodeId, configJson]);

  const update = (patch: Record<string, unknown>) => {
    const next = { ...cfg, ...patch };
    setCfg(next);
    onChange(nodeId, JSON.stringify(next));
  };

  switch (type) {
    case FlowNodeType.TriggerNewConversation:
      return (
        <p className="wt-rf-props-hint">
          Este gatilho dispara quando o contato envia a primeira mensagem (sem execução ativa).
        </p>
      );

    case FlowNodeType.TriggerKeyword: {
      const keywords = (cfg.keywords as string[] | undefined) ?? [];
      const text = keywords.join(', ');
      return (
        <Field label="Palavras-chave (separadas por vírgula)">
          <input
            className="wt-rf-input"
            value={text}
            onChange={(e) =>
              update({
                keywords: e.target.value
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean),
              })
            }
            placeholder="ex: preco, comprar, valor"
          />
        </Field>
      );
    }

    case FlowNodeType.SendMessage:
      return (
        <Field label="Mensagem">
          <MessageTextarea
            value={(cfg.message as string) ?? ''}
            onChange={(v) => update({ message: v })}
          />
        </Field>
      );

    case FlowNodeType.AskQuestion:
      return (
        <>
          <Field label="Pergunta">
            <MessageTextarea
              value={(cfg.question as string) ?? ''}
              onChange={(v) => update({ question: v })}
            />
          </Field>
          <Field label="Variável para guardar resposta">
            <input
              className="wt-rf-input wt-rf-mono"
              value={(cfg.variableName as string) ?? ''}
              onChange={(e) => update({ variableName: e.target.value })}
              placeholder="ex: nome_cliente"
            />
          </Field>
          <InactivityTimeoutField cfg={cfg} update={update} />
        </>
      );

    case FlowNodeType.Menu: {
      const options = (cfg.options as { label: string; value: string }[] | undefined) ?? [];
      return (
        <>
          <Field label="Mensagem do menu">
            <MessageTextarea
              value={(cfg.message as string) ?? ''}
              onChange={(v) => update({ message: v })}
            />
          </Field>
          <Field label="Opções">
            <div className="wt-rf-menu-editor">
              {options.map((opt, i) => (
                <div key={i} className="wt-rf-menu-row">
                  <input
                    className="wt-rf-input"
                    placeholder="Label exibido"
                    value={opt.label}
                    onChange={(e) => {
                      const next = [...options];
                      next[i] = { ...opt, label: e.target.value };
                      update({ options: next });
                    }}
                  />
                  <input
                    className="wt-rf-input wt-rf-mono"
                    placeholder="valor"
                    value={opt.value}
                    onChange={(e) => {
                      const next = [...options];
                      next[i] = { ...opt, value: e.target.value };
                      update({ options: next });
                    }}
                  />
                  <button
                    className="wt-rf-btn-ghost"
                    onClick={() => update({ options: options.filter((_, j) => j !== i) })}
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                className="wt-rf-btn-ghost"
                onClick={() =>
                  update({
                    options: [...options, { label: '', value: String(options.length + 1) }],
                  })
                }
              >
                + adicionar opção
              </button>
            </div>
          </Field>
          <InactivityTimeoutField cfg={cfg} update={update} />
        </>
      );
    }

    case FlowNodeType.TransferToAgent: {
      const upstreamQueueIds = findUpstreamQueueIds(nodeId, allNodes, allEdges);
      const hasUpstreamFilter = upstreamQueueIds.size > 0;
      const filteredAgents = hasUpstreamFilter
        ? lookups.agents.filter((a) => a.queueIds.some((qid) => upstreamQueueIds.has(qid)))
        : lookups.agents;
      const queueNames = hasUpstreamFilter
        ? Array.from(upstreamQueueIds)
            .map((qid) => lookups.queues.find((q) => q.id === qid)?.name ?? qid)
            .join(', ')
        : null;

      return (
        <>
          {hasUpstreamFilter && (
            <p className="wt-rf-props-hint">
              &#9432; Mostrando apenas usuários da{queueNames && queueNames.includes(',') ? 's filas' : ' fila'}: <strong>{queueNames}</strong>
            </p>
          )}
          <Field label="Usuário">
            <select
              className="wt-rf-input"
              value={(cfg.assigneeId as string) ?? ''}
              onChange={(e) => update({ assigneeId: e.target.value })}
            >
              <option value="">— Selecione —</option>
              {filteredAgents.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Mensagem de transferência (opcional)">
            <MessageTextarea
              value={(cfg.message as string) ?? ''}
              onChange={(v) => update({ message: v })}
            />
          </Field>
        </>
      );
    }

    case FlowNodeType.TransferToQueue:
      return (
        <>
          <Field label="Fila">
            <select
              className="wt-rf-input"
              value={(cfg.queueId as string) ?? ''}
              onChange={(e) => update({ queueId: e.target.value })}
            >
              <option value="">— Selecione —</option>
              {lookups.queues.map((q) => (
                <option key={q.id} value={q.id}>{q.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Mensagem de transferência (opcional)">
            <MessageTextarea
              value={(cfg.message as string) ?? ''}
              onChange={(v) => update({ message: v })}
            />
          </Field>
        </>
      );

    case FlowNodeType.AiAgent:
      return (
        <>
          <Field label="System prompt">
            <textarea
              className="wt-rf-input wt-rf-textarea"
              style={{ minHeight: 120 }}
              value={(cfg.systemPrompt as string) ?? ''}
              onChange={(e) => update({ systemPrompt: e.target.value })}
            />
          </Field>
          <Field label="Máximo de turnos">
            <input
              type="number"
              className="wt-rf-input wt-rf-mono"
              min={1}
              max={50}
              value={(cfg.maxTurns as number | undefined) ?? 10}
              onChange={(e) => update({ maxTurns: parseInt(e.target.value, 10) || 10 })}
            />
          </Field>
        </>
      );

    case FlowNodeType.Condition:
      return (
        <Field label="Expressão">
          <input
            className="wt-rf-input wt-rf-mono"
            value={(cfg.expression as string) ?? ''}
            onChange={(e) => update({ expression: e.target.value })}
          />
        </Field>
      );

    case FlowNodeType.SetTag:
      return (
        <Field label="Etiqueta">
          {lookups.tags.length === 0 ? (
            <p className="wt-rf-props-hint">Nenhuma etiqueta cadastrada.</p>
          ) : (
            <select
              className="wt-rf-input"
              value={(cfg.tagId as string) ?? ''}
              onChange={(e) => update({ tagId: e.target.value })}
            >
              <option value="">— Selecione —</option>
              {lookups.tags.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          )}
        </Field>
      );

    case FlowNodeType.SetCategory:
      return (
        <Field label="Categoria">
          {lookups.categories.length === 0 ? (
            <p className="wt-rf-props-hint">Nenhuma categoria cadastrada.</p>
          ) : (
            <select
              className="wt-rf-input"
              value={(cfg.categoryId as string) ?? ''}
              onChange={(e) => update({ categoryId: e.target.value })}
            >
              <option value="">— Selecione —</option>
              {lookups.categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}
        </Field>
      );

    case FlowNodeType.Webhook:
      return (
        <Field label="URL">
          <input
            className="wt-rf-input wt-rf-mono"
            value={(cfg.url as string) ?? ''}
            onChange={(e) => update({ url: e.target.value })}
          />
        </Field>
      );

    case FlowNodeType.Wait: {
      const seconds = (cfg.durationSeconds as number | undefined) ?? 5;
      return (
        <Field label="Aguardar (segundos)">
          <input
            type="number"
            className="wt-rf-input wt-rf-mono"
            min={1}
            value={seconds}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10) || 1;
              update({ durationSeconds: n, waitType: 'time' });
            }}
          />
        </Field>
      );
    }

    case FlowNodeType.WaitForReply: {
      const minutes = (cfg.timeoutMinutes as number | undefined) ?? 10;
      return (
        <>
          <Field label="Tempo de espera (minutos)">
            <input
              type="number"
              className="wt-rf-input wt-rf-mono"
              min={1}
              value={minutes}
              onChange={(e) => update({ timeoutMinutes: parseInt(e.target.value, 10) || 1 })}
            />
          </Field>
          <p className="wt-rf-props-hint">
            Se o cliente não responder nesse tempo, o fluxo segue pela saída <strong>sem resposta</strong>.
            Conecte essa saída a uma mensagem e/ou ao nó <strong>Encerrar atendimento</strong>.
          </p>
        </>
      );
    }

    case FlowNodeType.ResolveTicket:
      return (
        <>
          <Field label="Categoria ao encerrar">
            {lookups.categories.length === 0 ? (
              <p className="wt-rf-props-hint">Nenhuma categoria cadastrada. Cadastre uma para o encerramento automático funcionar.</p>
            ) : (
              <select
                className="wt-rf-input"
                value={(cfg.categoryId as string) ?? ''}
                onChange={(e) => update({ categoryId: e.target.value })}
              >
                <option value="">— Selecione —</option>
                {lookups.categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Mensagem ao encerrar (opcional)">
            <MessageTextarea
              value={(cfg.message as string) ?? ''}
              onChange={(v) => update({ message: v })}
            />
          </Field>
          <p className="wt-rf-props-hint">
            Aplica a categoria e encerra o atendimento. A categoria é obrigatória — sem ela o ticket não é encerrado.
          </p>
        </>
      );

    default:
      return <p>Sem configuração específica.</p>;
  }
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="wt-rf-field">
      <label className="wt-rf-field-label">{label}</label>
      {children}
    </div>
  );
}

/**
 * Variáveis de sistema disponíveis em qualquer texto enviado ao cliente. O motor
 * (FlowExecutionService.ReplaceVariablesAsync) substitui o token pelo valor do contato —
 * sem precisar de um nó "Fazer pergunta" antes. Adicionar um item aqui basta para o chip
 * aparecer em todos os campos de mensagem.
 */
const SYSTEM_VARIABLES: { token: string; label: string; description: string }[] = [
  {
    token: '{{contato.nome}}',
    label: 'Nome do contato',
    description: 'Substituído automaticamente pelo nome cadastrado do contato.',
  },
];

/**
 * Textarea de mensagem com chips clicáveis das variáveis disponíveis. Clicar no chip
 * insere o token na posição do cursor (ou no fim, se o textarea não estiver focado),
 * para o usuário descobrir e usar a variável sem decorar a sintaxe.
 */
function MessageTextarea({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  const insertToken = (token: string) => {
    const el = ref.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const next = value.slice(0, start) + token + value.slice(end);
    onChange(next);
    // Reposiciona o cursor logo após o token inserido no próximo frame (após o re-render).
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  return (
    <>
      <textarea
        ref={ref}
        className="wt-rf-input wt-rf-textarea"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      <div className="wt-rf-vars">
        <span className="wt-rf-vars-label">Inserir variável:</span>
        {SYSTEM_VARIABLES.map((v) => (
          <button
            type="button"
            key={v.token}
            className="wt-rf-var-chip"
            title={`${v.token} — ${v.description}`}
            onClick={() => insertToken(v.token)}
          >
            {v.label}
          </button>
        ))}
      </div>
    </>
  );
}

/**
 * Campo opcional de timeout de inatividade para nós que aguardam resposta (Menu, Fazer pergunta).
 * Com timeoutMinutes > 0, o nó passa a expor a saída "sem resposta" no canvas.
 */
function InactivityTimeoutField({
  cfg,
  update,
}: {
  cfg: Record<string, unknown>;
  update: (patch: Record<string, unknown>) => void;
}) {
  const minutes = (cfg.timeoutMinutes as number | undefined) ?? 0;
  return (
    <>
      <Field label="Encerrar por inatividade após (min)">
        <input
          type="number"
          className="wt-rf-input wt-rf-mono"
          min={0}
          value={minutes || ''}
          placeholder="0 = sem timeout"
          onChange={(e) => update({ timeoutMinutes: parseInt(e.target.value, 10) || 0 })}
        />
      </Field>
      {minutes > 0 && (
        <p className="wt-rf-props-hint">
          O nó passa a ter a saída <strong>sem resposta</strong>. Ligue-a a uma mensagem e/ou ao nó <strong>Encerrar atendimento</strong>.
        </p>
      )}
    </>
  );
}
